import { useEffect, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Building2, CheckCircle, Loader2, Mail, UserCheck, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/hooks/use-auth';
import { useGetInvitationByToken } from '@/api/organizations/get-invitation-by-token';
import { useAcceptInvitation } from '@/api/organizations/accept-invitation';
import { formatDate } from '@/utils/date';

const INVITATION_TOKEN_KEY = 'pending_invitation_token';

export const Route = createFileRoute('/accept-invitation')({
  component: AcceptInvitationPage,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      token: (search.token as string) || ''
    };
  }
});

function AcceptInvitationPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { token } = Route.useSearch();

  const [isAccepting, setIsAccepting] = useState(false);
  const [acceptSuccess, setAcceptSuccess] = useState(false);

  const invitationQuery = useGetInvitationByToken(token);
  const acceptInvitationMutation = useAcceptInvitation();

  useEffect(() => {
    if (!isAuthenticated && token) {
      localStorage.setItem(INVITATION_TOKEN_KEY, token);
      navigate({ to: '/login', search: { invitationToken: token } });
    }
  }, [isAuthenticated, token, navigate]);

  const handleAcceptInvitation = async () => {
    if (!token) {
      toast.error("Token d'invitation manquant");
      return;
    }

    setIsAccepting(true);
    try {
      const result = await acceptInvitationMutation.mutateAsync({ token });
      setAcceptSuccess(true);
      toast.success('Invitation acceptée avec succès!');

      localStorage.removeItem(INVITATION_TOKEN_KEY);

      setTimeout(() => {
        navigate({ to: `/organizations/${result.organizationId}` });
      }, 2000);
    } catch {
      toast.error("Erreur lors de l'acceptation de l'invitation");
      setIsAccepting(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <Loader2 className="h-12 w-12 animate-spin text-primary" />
            </div>
            <CardTitle>Redirection...</CardTitle>
            <CardDescription>Vous allez être redirigé vers la page de connexion.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (invitationQuery.isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <Loader2 className="h-12 w-12 animate-spin text-primary" />
            </div>
            <CardTitle>Vérification de l'invitation</CardTitle>
            <CardDescription>Veuillez patienter...</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (invitationQuery.isError) {
    const status = (invitationQuery.error as { status?: number }).status;
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-destructive">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <XCircle className="h-12 w-12 text-destructive" />
            </div>
            <CardTitle className="text-destructive">Invitation invalide</CardTitle>
            <CardDescription>
              {status === 404
                ? "Cette invitation n'existe pas ou a expiré."
                : "Une erreur est survenue lors de la vérification de l'invitation."}
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button onClick={() => navigate({ to: '/ideas' })}>Retour à l'accueil</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const invitation = invitationQuery.data;

  if (!invitation) {
    return null;
  }

  if (invitation.status !== 'Pending') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-destructive">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <XCircle className="h-12 w-12 text-destructive" />
            </div>
            <CardTitle className="text-destructive">Invitation {invitation.status.toLowerCase()}</CardTitle>
            <CardDescription>
              {invitation.status === 'Accepted'
                ? 'Cette invitation a déjà été acceptée.'
                : invitation.status === 'Revoked'
                  ? 'Cette invitation a été révoquée.'
                  : 'Cette invitation a expiré.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button onClick={() => navigate({ to: '/ideas' })}>Retour à l'accueil</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (acceptSuccess) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-primary">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <CheckCircle className="h-12 w-12 text-primary" />
            </div>
            <CardTitle>Invitation acceptée!</CardTitle>
            <CardDescription>Vous êtes maintenant membre de l'organisation.</CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Redirection vers l'organisation...</p>
              <Loader2 className="h-6 w-6 animate-spin mx-auto text-primary" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isAccepting) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <Loader2 className="h-12 w-12 animate-spin text-primary" />
            </div>
            <CardTitle>Acceptation de l'invitation</CardTitle>
            <CardDescription>Veuillez patienter...</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <Mail className="h-12 w-12 text-primary" />
          </div>
          <CardTitle>Invitation à rejoindre une organisation</CardTitle>
          <CardDescription>Vous avez été invité à rejoindre une organisation.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 bg-muted rounded-lg">
              <Building2 className="h-8 w-8 text-primary" />
              <div className="flex-1">
                <h3 className="font-semibold">{invitation.organizationName}</h3>
                <p className="text-sm text-muted-foreground">Organisation</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-4 bg-muted rounded-lg">
              <UserCheck className="h-8 w-8 text-primary" />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold">Rôle:</span>
                  <Badge>{invitation.role}</Badge>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {invitation.role === 'Admin'
                    ? 'Peut gérer les membres et les paramètres'
                    : 'Peut créer et modifier des projets'}
                </p>
              </div>
            </div>

            <div className="space-y-2 text-sm text-muted-foreground">
              <p>
                <strong>Email:</strong> {invitation.email}
              </p>
              <p>
                <strong>Expire le:</strong> {formatDate(invitation.expiresAt)}
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Button onClick={handleAcceptInvitation} disabled={acceptInvitationMutation.isPending} className="w-full">
              {acceptInvitationMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Acceptation...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Accepter l'invitation
                </>
              )}
            </Button>
            <Button variant="outline" onClick={() => navigate({ to: '/ideas' })} className="w-full">
              Refuser
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
